<script>
  import { createEventDispatcher } from "svelte";
  import {
    buildDriverSearchMatches,
    normalizeDriverSearchValue,
    resolveDriverSearchSelection,
  } from "./driver-search.js";

  export let entries = [];
  export let placeholder = "Buscar";
  export let ariaLabel = "Buscar conductor";
  export let emptyLabel = "Sin coincidencias";

  const dispatch = createEventDispatcher();

  let activeIndex = -1;
  let dropdownVisible = false;
  let inputValue = "";
  let allowHighlightedCommit = false;
  let matches = [];
  let rootElement;

  $: showDefaults =
    dropdownVisible && normalizeDriverSearchValue(inputValue).length === 0;
  $: matches = buildDriverSearchMatches(entries, inputValue, showDefaults);
  $: {
    if (!dropdownVisible || matches.length === 0) {
      activeIndex = -1;
    } else if (activeIndex === -1 || activeIndex >= matches.length) {
      activeIndex = 0;
    }
  }

  function openDropdown() {
    dropdownVisible = true;
  }

  function closeDropdown() {
    dropdownVisible = false;
    activeIndex = -1;
    allowHighlightedCommit = false;
  }

  function commitSelection(entry) {
    if (!entry) {
      return false;
    }
    inputValue = "";
    closeDropdown();
    dispatch("select", entry);
    return true;
  }

  function resolveCurrentSelection() {
    return resolveDriverSearchSelection(entries, inputValue);
  }

  function handleBlur(event) {
    const nextTarget = event.relatedTarget;
    if (rootElement && nextTarget && rootElement.contains(nextTarget)) {
      return;
    }
    const selectedEntry = resolveCurrentSelection();
    if (selectedEntry) {
      commitSelection(selectedEntry);
      return;
    }
    closeDropdown();
  }

  function handleInput(event) {
    inputValue = event.target.value || "";
    allowHighlightedCommit = false;
    openDropdown();
  }

  function handleKeydown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openDropdown();
      if (matches.length > 0) {
        activeIndex = activeIndex < matches.length - 1 ? activeIndex + 1 : 0;
        allowHighlightedCommit = true;
      }
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (matches.length > 0) {
        activeIndex = activeIndex > 0 ? activeIndex - 1 : matches.length - 1;
        allowHighlightedCommit = true;
      }
      return;
    }

    if (event.key === "Enter") {
      const selectedEntry =
        resolveCurrentSelection() ||
        (matches.length === 1
          ? matches[0]
          : allowHighlightedCommit && activeIndex >= 0 && matches[activeIndex]
            ? matches[activeIndex]
            : null);
      if (selectedEntry) {
        event.preventDefault();
        commitSelection(selectedEntry);
      }
      return;
    }

    if (event.key === "Escape") {
      closeDropdown();
    }
  }
</script>

<div class="driver-list-search" bind:this={rootElement}>
  <input
    class="driver-list-search__input"
    bind:value={inputValue}
    type="search"
    placeholder={placeholder}
    aria-label={ariaLabel}
    autocomplete="off"
    on:focus={openDropdown}
    on:input={handleInput}
    on:keydown={handleKeydown}
    on:blur={handleBlur}
  />
  {#if dropdownVisible}
    <div class="driver-list-search__menu">
      {#if matches.length > 0}
        {#each matches as entry, index}
          <button
            type="button"
            class="driver-list-search__option"
            class:driver-list-search__option--active={index === activeIndex}
            aria-label={entry.searchValue}
            on:mousedown|preventDefault
            on:mouseenter={() => {
              activeIndex = index;
              allowHighlightedCommit = true;
            }}
            on:click={() => {
              commitSelection(entry);
            }}
          >
            <span class="driver-list-search__name">{entry.name}</span>
          </button>
        {/each}
      {:else if normalizeDriverSearchValue(inputValue).length > 0}
        <div class="driver-list-search__empty">{emptyLabel}</div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .driver-list-search {
    position: relative;
    width: 100%;
    min-width: 0;
  }
  .driver-list-search__input {
    width: 100%;
    min-height: 1.9rem;
    padding: 0.2rem 0.55rem;
    border: none;
    border-radius: 0.35rem;
    font-size: 0.82rem;
    color: #212529;
  }
  .driver-list-search__input::placeholder {
    color: #6c757d;
  }
  .driver-list-search__input:focus {
    outline: 2px solid rgba(33, 150, 243, 0.65);
    box-shadow: none;
  }
  .driver-list-search__menu {
    position: absolute;
    top: calc(100% + 0.35rem);
    right: 0;
    left: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    overflow: auto;
    max-height: 14rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    background: #ffffff;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.22);
  }
  .driver-list-search__option {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.45rem 0.6rem;
    border: none;
    border-bottom: 1px solid #e5e7eb;
    background: transparent;
    text-align: left;
    cursor: pointer;
    color: #111827;
  }
  .driver-list-search__option:last-child {
    border-bottom: none;
  }
  .driver-list-search__option:hover,
  .driver-list-search__option--active {
    background: #eff6ff;
  }
  .driver-list-search__name {
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.25;
  }
  .driver-list-search__empty {
    padding: 0.55rem 0.65rem;
    font-size: 0.76rem;
    color: #64748b;
  }
</style>
